/**
 * Custom (Miss Sophie): bridge between the Wishlist Engine app and Horizon's cart.
 * Loaded from snippets/custom-wishlist-header.liquid while Theme settings > Integrations > "Enable wishlist" is on.
 *
 * Problem: after its own /cart/add.js the app "refreshes" a Dawn-style cart. It requests the sections
 * cart-drawer / cart-icon-bubble (which don't exist in Horizon, so Shopify returns null) and then writes
 * the result into #cart-drawer. That leaves the text "undefined" inside <theme-drawer id="cart-drawer">,
 * the <dialog> is gone and the next cart click throws "showModal … The element is not in a Document".
 * The cart count never updates either, because Horizon only listens to shopify:cart:lines-update.
 *
 * Fix, without touching Horizon or the app:
 * 1. window.wishlistEngineCallback: when it exists, the app skips its refresh on a single "Add to cart"
 *    and calls it instead. We announce the add with Horizon's standard shopify:cart:lines-update event,
 *    like product-form.js: the cart bubble updates, the drawer re-renders and auto-opens (theme setting).
 * 2. "Add all to cart" runs the app's refresh before the callback. The app reads its section list from
 *    __this._getSectionsToRender() at click time, so we empty that list just before its click handler runs
 *    (capture phase). Harmless if the app ever renames it.
 * 3. Safety net: if the drawer's <dialog> was wiped anyway, a fresh drawer is put back.
 *
 * Header heart (snippets/custom-wishlist-header.liquid): the bubble uses Horizon's cart bubble markup,
 * so it gets the same grow animation as the cart bubble whenever the app changes the number.
 * While the number is 0 the bubble is hidden.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const DRAWER_SECTION_ID = 'cart-drawer-section';
const root = window.Shopify?.routes?.root || '/';

/**
 * @param {string} url
 */
async function fetchJSON(url) {
  const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error(`${url} responded ${response.status}`);
  return response.json();
}

/** Stops the app's Dawn-style DOM refresh (also used by its "Add all to cart"). */
function disableAppSectionRefresh() {
  const app = /** @type {any} */ (window).__this;
  if (!app || typeof app._getSectionsToRender !== 'function' || app.__msSectionsDisabled) return;

  app._getSectionsToRender = () => [];
  app.__msSectionsDisabled = true;
}

/**
 * Puts a fresh <theme-drawer> back when its <dialog> was wiped.
 * @param {Record<string, string | null> | undefined} sections
 */
function restoreDrawer(sections) {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer || drawer.querySelector(':scope > dialog')) return;

  const html = sections?.[DRAWER_SECTION_ID];
  if (!html) return;

  const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('theme-drawer#cart-drawer');
  if (fresh) drawer.replaceWith(document.importNode(fresh, true));
}

/**
 * Called by the app with the /cart/add.js response after a successful add.
 * @param {any} response
 */
window.wishlistEngineCallback = async (response) => {
  try {
    const items = Array.isArray(response?.items) ? response.items : response?.variant_id ? [response] : [];
    const hasDrawer = Boolean(document.querySelector('theme-drawer#cart-drawer'));

    const [cart, sections] = await Promise.all([
      fetchJSON(`${root}cart.js`),
      hasDrawer ? fetchJSON(`${root}?sections=${DRAWER_SECTION_ID}`).catch(() => undefined) : undefined,
    ]);

    restoreDrawer(sections);

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((/** @type {any} */ item) => ({
          merchandiseId: String(item.variant_id ?? item.id),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: items.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + (item.quantity || 0), 0),
        sections,
        source: 'wishlist-engine',
        didError: false,
      },
    });
  } catch (error) {
    console.warn('[custom-wishlist-engine] Cart refresh failed:', error);
  }
};

/**
 * @param {string | null | undefined} value - The number the app wrote into the bubble
 * @returns {boolean} Whether the wishlist has items
 */
function hasItems(value) {
  return Number.parseInt(value ?? '', 10) > 0;
}

/**
 * Wishlist bubble: hidden while the app's number is 0 (custom-wishlist-header--has-items on the link,
 * see snippets/custom-wishlist-header.liquid), and the cart bubble animation
 * (header-actions.liquid: .cart-bubble--animating) whenever the app writes a different number into it.
 * Like the cart, no animation when it drops to 0.
 */
function animateWishlistBubbles() {
  for (const count of document.querySelectorAll('.custom-wishlist-header__count')) {
    const link = count.closest('.custom-wishlist-header');
    const bubble = count.closest('.cart-bubble');
    const text = count.closest('.cart-bubble__text');
    if (!link || !bubble || !text) continue;

    let lastValue = count.textContent?.trim();

    // The app may have written its number before this module ran
    link.classList.toggle('custom-wishlist-header--has-items', hasItems(lastValue));

    new MutationObserver(() => {
      const value = count.textContent?.trim();
      if (value === lastValue) return;
      lastValue = value;
      link.classList.toggle('custom-wishlist-header--has-items', hasItems(value));
      if (!hasItems(value)) return;

      bubble.classList.remove('cart-bubble--animating');
      void bubble.offsetWidth; // restart the animation when the number changes again mid-animation
      bubble.classList.add('cart-bubble--animating');
    }).observe(count, { childList: true, characterData: true, subtree: true });

    text.addEventListener('animationend', () => bubble.classList.remove('cart-bubble--animating'));
  }
}

disableAppSectionRefresh();
// The app may create its object later; capture phase runs before the app's own click handlers.
document.addEventListener('click', disableAppSectionRefresh, true);
animateWishlistBubbles();
